import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum EnemyType {
  INFANTRY = 'INFANTRY',
  SHIELD_BEARER = 'SHIELD_BEARER',
  ARCHER = 'ARCHER',
  ENGINEER = 'ENGINEER',
  SIEGE = 'SIEGE',
  BOSS = 'BOSS',
}

export interface Enemy {
  id: string;
  type: EnemyType;
  health: number;
  maxHealth: number;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  speed: number;
  damage: number;
  isAlive: boolean;
  targetPosition?: { x: number; y: number; z: number };
}

export interface Wave {
  number: number;
  totalEnemies: number;
  remainingEnemies: number;
  enemyTypes: EnemyType[];
  difficulty: number;
  timeLimit?: number;
  bossWave: boolean;
}

/**
 * Interface for wave state
 */
interface WaveState {
  currentWave: number;
  enemiesRemaining: number;
  enemiesDefeated: number;
  waveActive: boolean;
  totalEnemiesDefeated: number;
  highestWaveReached: number;
}

/**
 * Interface for wave info update
 */
interface WaveInfoUpdate {
  currentWave: number;
  enemiesRemaining: number;
  enemiesDefeated: number;
  waveActive: boolean;
}

/**
 * Initial wave state
 */
const initialState: WaveState = {
  currentWave: 0,
  enemiesRemaining: 0,
  enemiesDefeated: 0,
  waveActive: false,
  totalEnemiesDefeated: 0,
  highestWaveReached: 0
};

/**
 * Wave slice for Redux
 */
const waveSlice = createSlice({
  name: 'wave',
  initialState,
  reducers: {
    /**
     * Updates the wave information
     */
    updateWaveInfo: (state, action: PayloadAction<WaveInfoUpdate>) => {
      const { currentWave, enemiesRemaining, enemiesDefeated, waveActive } = action.payload;
      
      state.currentWave = currentWave;
      state.enemiesRemaining = enemiesRemaining;
      state.enemiesDefeated = enemiesDefeated;
      state.waveActive = waveActive;
      
      // Update total enemies defeated when a wave is completed
      if (!waveActive && state.enemiesDefeated > 0) {
        state.totalEnemiesDefeated += state.enemiesDefeated;
      }
      
      // Update highest wave reached
      if (currentWave > state.highestWaveReached) {
        state.highestWaveReached = currentWave;
      }
    },
    
    /**
     * Resets the wave state
     */
    resetWaveState: (state) => {
      state.currentWave = 0;
      state.enemiesRemaining = 0;
      state.enemiesDefeated = 0;
      state.waveActive = false;
    },
    
    /**
     * Resets all wave statistics
     */
    resetWaveStats: (state) => {
      state.totalEnemiesDefeated = 0;
      state.highestWaveReached = 0;
    }
  }
});

export const { updateWaveInfo, resetWaveState, resetWaveStats } = waveSlice.actions;
export default waveSlice.reducer; 